import React from 'react'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { AddCircleOutline } from '@mui/icons-material'
import { TppRow as TppRowType, TppCell, TppColumn, TppSlot as TppSlotType, DRAG_TYPES } from '../types'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import TppSlot from './TppSlot'
import { SvgIcon } from '@mui/material'
import { IconButton } from '@mui/material/'
import { ColumnVersionFull } from '../../TPPChangeLog/types'
import { ReactComponent as InfoCircle } from '../../../assets/images/info-circle.svg'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'

interface TppRowProps {
    row: TppRowType
    slots: Record<string, TppSlotType>
    columnIds: string[]
    cellLookup: Record<string, Record<string, TppCell>>
    pinnedIds: string[]
    columnsById: Record<string, TppColumn>
    cellsById: Record<string, TppCell>
    onAddSlot: (rowId: string) => void
    isAddingSlot?: boolean
    isSummary: boolean
    activeDragType: string | null
    activeCellSlotId: string | null
    visibleColumns: { id: string; label: string }[]
    creatingCellKey: string | null
    onCreateCell: (slotId: string, columnId: string) => Promise<void>
    movingCellId: string | null
    movingSlotId: string | null
    showContext: boolean
    onUpdateSlotContext: (slotId: string, context: string) => Promise<void>
    sectionTitle?: string
    comparisonData?: ColumnVersionFull | null
    isArchivedTab?: boolean
    canEdit?: boolean
}

const TppRow: React.FC<TppRowProps> = ({
    row,
    slots,
    columnIds,
    cellLookup,
    pinnedIds,
    columnsById,
    cellsById,
    onAddSlot,
    isAddingSlot,
    isSummary,
    activeDragType,
    activeCellSlotId,
    visibleColumns,
    creatingCellKey,
    onCreateCell,
    movingCellId,
    movingSlotId,
    showContext,
    onUpdateSlotContext,
    sectionTitle,
    comparisonData,
    isArchivedTab,
    canEdit = true,
}) => {
    const layout = useLayoutConstants()
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: row._id,
        disabled: true,
        data: { type: DRAG_TYPES.ROW, sectionId: row.sectionId, rowId: row._id },
    })

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        display: 'flex',
        borderBottom: '1px solid',
        borderColor: 'figma.grayscale.stroke',
        position: 'relative' as const,
        width: 'max-content',
        zIndex: 10,
    }

    return (
        <Stack direction="row" ref={setNodeRef} sx={style}>
            {/* STICKY ROW TITLE */}
            <Stack
                direction="column"
                justifyContent="space-between"
                alignItems="flex-start"
                {...attributes}
                {...listeners}
                sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 30, // High Z-Index to stay above scrolling content
                    width: layout.ROW_HEADER_WIDTH,
                    minWidth: layout.ROW_HEADER_WIDTH,
                    borderRight: '1px solid',
                    borderColor: 'figma.grayscale.stroke',
                    padding: '16px',
                    // cursor: 'grab',
                    bgcolor: 'figma.grayscale.contentWrapper',
                }}
            >
                <Stack direction="column">
                    {sectionTitle && (
                        <Typography variant="18x500" color="figma.grayscale.textHier1" mt={-1}>
                            {sectionTitle}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="16x400" color="figma.grayscale.textHier2">
                            {row.title}
                        </Typography>

                        {row.info && row.info.trim().length > 0 && (
                            <Tooltip
                                arrow
                                placement="bottom"
                                title={
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            maxWidth: 280,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        <Typography variant="14x400" color="figma.grayscale.textHier1">
                                            {row.info}
                                        </Typography>
                                    </Box>
                                }
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: 'figma.grayscale.contentWrapper',
                                            boxShadow: '0px 4px 12px rgba(0,0,0,0.18)',
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: 'figma.grayscale.contentWrapper',
                                            //boxShadow: '0px 4px 12px rgba(0,0,0,0.18)',
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: 'figma.grayscale.tier2',
                                        cursor: 'default',
                                        '&:hover': { color: 'figma.primary' },
                                    }}
                                >
                                    <SvgIcon component={InfoCircle} inheritViewBox sx={{ fontSize: '16px' }} />
                                </Box>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <IconButton
                    disableRipple
                    onClick={() => onAddSlot(row._id)}
                    disabled={isAddingSlot || isArchivedTab || isSummary || !canEdit}
                    size="small"
                    sx={(theme) => ({
                        p: 1.5,
                        ml: -1.5,
                        color: 'figma.primary',
                        '&.Mui-disabled': {
                            color: 'figma.primary2',
                        },
                    })}
                >
                    <Stack direction="row" gap={1} alignItems="center">
                        <SvgIcon
                            component={AddCircleOutline}
                            inheritViewBox
                            fontSize="small"
                            color="inherit"
                            sx={{ fontSize: '20px' }}
                        />
                        <Typography flex={1} variant="16x600" color="inherit" textTransform="uppercase" mt="-3px">
                            Add slot
                        </Typography>
                    </Stack>
                </IconButton>
            </Stack>

            <Stack direction="column">
                <SortableContext items={row.slotIds} strategy={verticalListSortingStrategy}>
                    {row.slotIds.map((slotId, index) => (
                        <TppSlot
                            key={slotId}
                            slotId={slotId}
                            rowId={row._id}
                            slot={slots[slotId]}
                            columnIds={columnIds}
                            cellLookup={cellLookup}
                            pinnedIds={pinnedIds}
                            isSummary={isSummary}
                            activeDragType={activeDragType}
                            activeCellSlotId={activeCellSlotId}
                            visibleColumns={visibleColumns}
                            columnsById={columnsById}
                            cellsById={cellsById}
                            creatingCellKey={creatingCellKey}
                            onCreateCell={onCreateCell}
                            movingCellId={movingCellId}
                            movingSlotId={movingSlotId}
                            showContext={showContext}
                            onUpdateSlotContext={onUpdateSlotContext}
                            isLastSlot={index === row.slotIds.length - 1}
                            comparisonData={comparisonData}
                            isArchivedTab={isArchivedTab}
                            canEdit={canEdit}
                        />
                    ))}
                </SortableContext>
            </Stack>
        </Stack>
    )
}

export default TppRow
